import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import {
  Menu, X, LogOut,
  LayoutDashboard, Building2, Users, FileSignature, Briefcase, Clock,
  CalendarOff, Receipt, CreditCard, FileBarChart, FolderOpen,
  ShieldCheck, Sparkles, FileSearch, Stethoscope, Calculator, ScanLine, NotebookPen, Mail,
  BookUser, ClipboardList, Shield, UserCheck,
} from "lucide-react";

const NAV = [
  { to: "/app", icon: LayoutDashboard, label: "Tableau de bord", end: true, roles: "*" },
  { to: "/app/entreprises", icon: Building2, label: "Entreprises", roles: "*" },
  { to: "/app/registre-membres", icon: BookUser, label: "Registre des membres", roles: ["admin","gestionnaire_ge","finance","auditeur"] },
  { to: "/app/registre-salaries", icon: UserCheck, label: "Registre des salariés", roles: ["admin","gestionnaire_ge","finance","auditeur"] },
  { to: "/app/demandes", icon: FileSearch, label: "Demandes MAD", roles: ["admin","gestionnaire_ge","finance","employeur","auditeur"] },
  { to: "/app/salaries", icon: Users, label: "Salariés", roles: "*" },
  { to: "/app/conventions", icon: FileSignature, label: "Conventions", roles: "*" },
  { to: "/app/missions", icon: Briefcase, label: "Missions", roles: "*" },
  { to: "/app/timesheets", icon: Clock, label: "Feuilles de temps", roles: "*" },
  { to: "/app/absences", icon: CalendarOff, label: "Absences & Congés", roles: "*" },
  { to: "/app/frais", icon: Receipt, label: "Frais", roles: "*" },
  { to: "/app/credits", icon: CreditCard, label: "Abonnements & Crédits", roles: ["admin","gestionnaire_ge","finance","employeur","auditeur"] },
  { to: "/app/appels", icon: FileBarChart, label: "Appels à contribution", roles: ["admin","gestionnaire_ge","finance","employeur","auditeur"] },
  { to: "/app/comptabilite", icon: Calculator, label: "Comptabilité & FEC", roles: ["admin","gestionnaire_ge","finance","auditeur"] },
  { to: "/app/ged", icon: FolderOpen, label: "GED — Documents", roles: "*" },
  { to: "/app/medical", icon: Stethoscope, label: "Espace médical", roles: ["admin","medical"] },
  { to: "/app/qr-scans", icon: ScanLine, label: "Vérifications QR", roles: ["admin","gestionnaire_ge","finance","auditeur"] },
  { to: "/app/notes", icon: NotebookPen, label: "Notes terrain", roles: "*" },
  { to: "/app/taches", icon: ClipboardList, label: "Tâches", roles: "*" },
  { to: "/app/assistant", icon: Sparkles, label: "ZOE — Assistante", roles: ["admin","gestionnaire_ge","finance","employeur","medical","auditeur"] },
  { to: "/app/emails", icon: Mail, label: "Emails Brevo", roles: ["admin","gestionnaire_ge","finance","auditeur"] },
  { to: "/app/roles", icon: Shield, label: "Rôles & permissions", roles: ["admin"] },
  { to: "/app/audit", icon: ShieldCheck, label: "Journal d'audit", roles: ["admin","gestionnaire_ge","auditeur"] },
];

const ROLE_LABELS = {
  admin: "Administrateur", gestionnaire_ge: "Gestionnaire GE", finance: "Finance",
  employeur: "Employeur", salarie: "Salarié", missionnaire: "Missionnaire",
  medical: "Médical", auditeur: "Auditeur",
};

export default function MobileNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const visible = NAV.filter((i) => i.roles === "*" || (user && i.roles.includes(user.role)));

  const handleLogout = async () => { await logout(); navigate("/login"); };

  // Page courante pour le titre du header
  const current = visible.find((i) => (i.end ? location.pathname === i.to : location.pathname.startsWith(i.to)));
  const currentLabel = current?.label || "O'SCOP GE";

  return (
    <>
      {/* Barre supérieure mobile */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-slate-900 text-white h-14 flex items-center justify-between px-4 border-b border-slate-800" data-testid="mobile-header">
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-open" className="h-9 w-9 rounded-md hover:bg-slate-800 flex items-center justify-center" aria-label="Menu">
          <Menu className="h-5 w-5" />
        </button>
        <div className="text-sm font-semibold tracking-tight truncate px-2" style={{ fontFamily: "Outfit, sans-serif" }}>{currentLabel}</div>
        <NotificationBell />
      </header>

      {/* Drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-slate-900 text-slate-300 flex flex-col animate-in slide-in-from-left duration-200">
            <div className="p-4 border-b border-slate-800 flex items-center justify-between">
              <Logo variant="light" />
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" className="h-9 w-9 rounded-md hover:bg-slate-800 flex items-center justify-center" aria-label="Fermer">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
              {visible.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.to.split("/").pop() || "dashboard"}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                      isActive
                        ? "bg-slate-800/80 text-white border-l-2 border-[#C5A059] pl-[10px]"
                        : "hover:bg-slate-800/50 hover:text-white border-l-2 border-transparent"
                    }`
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span className="font-medium tracking-tight">{item.label}</span>
                </NavLink>
              ))}
            </nav>
            <div className="border-t border-slate-800 p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#C5A059] to-[#A88846] flex items-center justify-center text-white font-semibold">
                  {user?.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white truncate">{user?.name}</div>
                  <div className="text-xs text-slate-400 truncate">{ROLE_LABELS[user?.role] || user?.role}</div>
                </div>
              </div>
              <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-slate-800/60 hover:bg-slate-800 text-slate-300 hover:text-white transition-colors">
                <LogOut className="h-4 w-4" /> Se déconnecter
              </button>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
